import { useId, useState, type KeyboardEvent } from "react";
import { Button, Input, SearchField as AriaSearchField, type SearchFieldProps } from "react-aria-components";
import { twMerge } from "@/lib/twMerge";
import { FIELD_STYLES } from "@/lib/fieldStyles";
import { applySuggestion, fieldSuggestions, invalidValueHint, suggest, type FieldHints } from "@/lib/search";

interface Props extends Omit<SearchFieldProps, "value" | "onChange"> {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** The `field:value` words this search understands (lib/search.ts `fieldHints`). With them,
   * typing part of a field name suggests the field, and a field and colon suggests its values;
   * a value that doesn't exist says what the valid ones are. */
  hints?: FieldHints;
}

/** A search box with a clear (✕) button that appears once there's text, and — given `hints`
 * — suggestions for `field:value` words as they're typed: ↑/↓ to move, Enter or Tab to take
 * one, or click it. Clicking the empty box, or pressing ↓ in it, lists every field the
 * search understands, so they can be discovered. React Aria's SearchField clears on Escape and keeps focus in the box
 * after clearing. Styled with the shared FIELD_STYLES, like TextField. */
export function SearchField({ placeholder, className, hints, value, onChange, ...props }: Props) {
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  // Whether an empty box is listing every field it understands. Asked for (↓ or a click), not
  // automatic, so a page that focuses the box as it appears isn't covered by a menu.
  const [listing, setListing] = useState(false);
  const listId = useId();
  const options =
    !hints || !focused ? [] : value === "" ? (listing ? fieldSuggestions(hints) : []) : suggest(value, hints);
  const invalid = hints && focused ? invalidValueHint(value, hints) : null;
  const index = Math.min(active, Math.max(options.length - 1, 0));

  /** Takes a suggestion: replaces the word being typed with it. */
  function pick(i: number) {
    onChange(applySuggestion(value, options[i]));
    setActive(0);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (hints && value === "" && !listing && e.key === "ArrowDown") {
      e.preventDefault();
      setListing(true);
      return;
    }
    if (options.length === 0) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((index + (e.key === "ArrowDown" ? 1 : options.length - 1)) % options.length);
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pick(index);
    }
  }

  return (
    <AriaSearchField
      {...props}
      value={value}
      onChange={(v) => {
        onChange(v);
        setActive(0);
        setListing(false);
      }}
      className={twMerge("group relative flex items-center", className)}
    >
      <Input
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setListing(false);
        }}
        onClick={() => setListing(true)}
        onKeyDown={onKeyDown}
        role={hints ? "combobox" : undefined}
        aria-expanded={hints ? options.length > 0 : undefined}
        aria-controls={options.length > 0 ? listId : undefined}
        aria-activedescendant={options.length > 0 ? `${listId}-${index}` : undefined}
        aria-autocomplete={hints ? "list" : undefined}
        className={twMerge(FIELD_STYLES, "w-full pr-8 [&::-webkit-search-cancel-button]:hidden")}
      />
      <Button
        aria-label="Clear search"
        className="absolute right-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded text-text/50 group-data-[empty]:hidden hover:bg-sunken hover:text-text"
      >
        ✕
      </Button>
      {options.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          className="absolute top-full left-0 z-30 mt-1 max-h-72 w-full overflow-y-auto rounded border border-line bg-surface py-1 text-sm shadow-lg"
        >
          {options.map((o, i) => (
            <li
              key={o.label}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === index}
              // mousedown, not click: taking the suggestion before the input's blur closes the list.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(i);
              }}
              onMouseEnter={() => setActive(i)}
              className={twMerge("flex cursor-pointer items-baseline gap-2 px-3 py-1.5", i === index && "bg-sunken")}
            >
              <span className="font-mono text-xs">{o.label}</span>
              {o.detail && <span className="truncate text-xs text-text/50">{o.detail}</span>}
            </li>
          ))}
        </ul>
      )}
      {invalid && (
        <p
          role="status"
          className="absolute top-full left-0 z-30 mt-1 w-full rounded border border-warn-line bg-warn-bg px-3 py-1.5 text-xs text-warn shadow-lg"
        >
          {invalid}
        </p>
      )}
    </AriaSearchField>
  );
}
